import {useState} from "react";
import cn from "classnames";
import {useQueryClient} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";

import {API} from "api";
import {useSetting} from "contexts/setting";
import {getChannelSettingsQueryKey} from "hooks/useChannelSettings";
import {writeUrlBackground} from "libs/bgNumbering";
import {productOfFileName} from "./replicateBg";

/**
 * One picture per product, in one step: the admin drops the coloured copies
 * of a picture, named after the products ("splash_PyCharm.png", "bg_WS.png").
 * Each file becomes the FIRST background of its product's gallery for
 * everyone, in the set on screen on Releases — the same as "Save as default"
 * with an upload tile (POST /api/v3/bg-list/first). Then the admin positions
 * the picture on one product, saves it, and copies the position to the others
 * (ReplicateBgSection.tsx).
 */

interface Option {
  label: string;
  value: string;
}

interface BatchFile {
  file: File;
  product: string | null;
  state: "ready" | "uploading" | "done" | "failed";
  variation?: string;
  error?: string;
}

interface Props {
  channel: string;
  type: string;
  /** The releases set that alone gets the pictures; null for every set. */
  set: string | null;
  /** The products a file can be for: the page's product and the targets. */
  products: Option[];
  /** The product on screen. */
  product: string;
  disabled: boolean;
}

const IMAGE_TYPES = "image/png,image/jpeg,image/webp";

export default function BatchBgUpload({channel, type, set, products, product, disabled}: Props) {
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const {editBgOption} = useSetting() as unknown as {editBgOption: (value: string) => void};
  const [files, setFiles] = useState<BatchFile[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const update = (index: number, patch: Partial<BatchFile>) =>
    setFiles((previous) => previous.map((item, i) => (i === index ? {...item, ...patch} : item)));

  const onPick = (picked: FileList | null) => {
    if (!picked) return;
    setFiles(
      [...picked].map((file) => ({
        file,
        product: productOfFileName(file.name, products),
        state: "ready",
      }))
    );
  };

  const ready = files.filter((item) => item.state !== "done" && item.product);
  const chosen = ready.map((item) => item.product);
  const duplicate = chosen.find((code, index) => chosen.indexOf(code) !== index) ?? null;
  const labelOf = (code: string) => products.find((p) => p.value === code)?.label ?? code;

  const handleAdd = async () => {
    setIsRunning(true);
    try {
      for (const [index, item] of files.entries()) {
        if (item.state === "done" || !item.product) continue;
        update(index, {state: "uploading", error: undefined});
        try {
          const upload = await API.uploadFile(item.file);
          if (!upload?.id) throw new Error("Invalid upload response — id missing");
          const {variation} = await API.promoteBgUploadFirst({
            channel,
            type,
            product: item.product,
            uploadId: upload.id,
            variation: undefined,
            set: set ?? undefined,
          });
          if (variation === undefined || variation === null) {
            throw new Error("Invalid bg-list/first response — variation missing");
          }
          update(index, {state: "done", variation: String(variation)});
        } catch (error) {
          console.error(`[BatchBgUpload] ${item.file.name} failed:`, error);
          update(index, {
            state: "failed",
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    } finally {
      setIsRunning(false);
      // The galleries list the new pictures; the server can offer a hidden
      // one again (channel-settings.ts).
      await queryClient.invalidateQueries({queryKey: ["v3-backgrounds"]});
      await queryClient.invalidateQueries({queryKey: ["bg-list"]});
      await queryClient.invalidateQueries({queryKey: ["replica-catalog"]});
      await queryClient.invalidateQueries({queryKey: getChannelSettingsQueryKey(channel)});
    }
  };

  const shownHere = files.find((item) => item.state === "done" && item.product === product);

  return (
    <div className="replicate-bg-batch">
      <label className={cn("replicate-bg-change replicate-bg-upload", {"is-disabled": disabled})}>
        <input
          type="file"
          multiple
          accept={IMAGE_TYPES}
          disabled={disabled || isRunning}
          onChange={(event) => {
            onPick(event.target.files);
            event.target.value = "";
          }}
        />
        Add one picture per product…
      </label>
      {files.length > 0 && (
        <>
          <span className="replicate-bg-note">
            {`Each file becomes the first background of its product${set ? `, in this set only` : ""}, for everyone. The product comes from the file name.`}
          </span>
          <div className="replicate-bg-rows">
            {files.map((item, index) => (
              <div
                key={`${item.file.name}-${index}`}
                className={cn("replicate-bg-row replicate-bg-batch-row", {
                  "is-off": !item.product,
                })}
              >
                <span className="replicate-bg-product-label" title={item.file.name}>
                  {item.file.name}
                </span>
                <select
                  aria-label={`Product of ${item.file.name}`}
                  value={item.product ?? ""}
                  disabled={isRunning || item.state === "done"}
                  onChange={(event) => update(index, {product: event.target.value || null})}
                >
                  <option value="">— skip —</option>
                  {products.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span
                  className={cn("replicate-bg-match", {
                    "is-same": item.state === "done",
                    "is-similar": item.state === "failed",
                  })}
                  title={item.error}
                >
                  {item.state === "done"
                    ? "added"
                    : item.state === "uploading"
                      ? "…"
                      : item.state === "failed"
                        ? "failed"
                        : ""}
                </span>
              </div>
            ))}
          </div>
          {duplicate && (
            <span className="default-scene-status warning">
              {`Two files are for ${labelOf(duplicate)}: the second one would go first.`}
            </span>
          )}
          <div className="default-scene-row">
            <button
              type="button"
              className="default-scene-btn"
              onClick={handleAdd}
              disabled={disabled || isRunning || ready.length === 0 || Boolean(duplicate)}
            >
              {isRunning ? "..." : `Add to ${ready.length} product${ready.length === 1 ? "" : "s"}`}
            </button>
          </div>
          {shownHere?.variation && (
            <span className="default-scene-note">
              {"Position the new picture on this page, Save, then copy it below. "}
              <button
                type="button"
                className="replicate-bg-change"
                onClick={() => {
                  // As usePromoteUpload: by identity, the page numbers it.
                  editBgOption(shownHere.variation!);
                  setSearchParams((params) => writeUrlBackground(params, shownHere.variation!, null));
                }}
              >
                Show it here
              </button>
            </span>
          )}
        </>
      )}
    </div>
  );
}
